/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { ReceiptSummary } from "../Purchases/types";
import type { Invoice, InvoiceDraft, InvoiceLine, InvoiceStatus } from "./types";
import { addDays, daysUntil, formatDay, money, todayIso } from "./format";
import { emptyInvoiceLine, InvoiceLinesTable, lineTotal } from "./InvoiceLinesTable";
import { LinkedDeliveries } from "./LinkedDeliveries";
import { MarkPaidModal, type Payment } from "./MarkPaidModal";

type Supplier = { id: number; name: string };

type Props = {
  /** Existing invoice to open, or null for a new one. */
  invoiceId: number | null;
  /** A new invoice pre-filled from a scan. */
  initial?: InvoiceDraft | null;
  /** Deliveries to link to a new invoice. */
  initialReceiptIds?: number[];
  onClose: (changed: boolean) => void;
};

const TERMS = [0, 10, 14, 30, 60];

const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));

// Enter or review a supplier invoice: who, number, dates, amounts (or lines),
// the deliveries it bills; then mark it paid.
// See docs/plans/inventory_and_costing/05_purchasing.md.
export const InvoiceEditor = defineComponent<Props>({
  name: "InvoiceEditor",
  props: ["invoiceId", "initial", "initialReceiptIds", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const busy = ref(false);
    const changed = ref(false);
    const payOpen = ref(false);

    const id = ref<number | null>(props.invoiceId);
    const status = ref<InvoiceStatus>("open");
    const invoice = ref<Invoice | null>(null);
    const supplierId = ref<number | null>(null);
    const number = ref("");
    const issueDate = ref(todayIso());
    const dueDate = ref<string>(addDays(todayIso(), 30));
    const currency = ref("");
    const net = ref<number | null>(null);
    const vat = ref<number | null>(null);
    const total = ref<number | null>(null);
    const notes = ref("");
    const documentKey = ref<string | null>(null);
    const lines = ref<InvoiceLine[]>([]);
    const receiptIds = ref<number[]>(props.initialReceiptIds ?? []);

    const suppliers = ref<Supplier[]>([]);
    const receipts = ref<ReceiptSummary[]>([]);

    const readOnly = computed(() => status.value !== "open");

    const apply = (inv: Invoice) => {
      invoice.value = inv;
      id.value = inv.id;
      status.value = inv.status;
      supplierId.value = inv.supplier_id;
      number.value = inv.invoice_number ?? "";
      issueDate.value = inv.issue_date.slice(0, 10);
      dueDate.value = inv.due_date?.slice(0, 10) ?? "";
      currency.value = inv.currency ?? "";
      net.value = inv.net_amount;
      vat.value = inv.vat_amount;
      total.value = inv.total;
      notes.value = inv.notes ?? "";
      documentKey.value = inv.document_key;
      lines.value = inv.lines.map((l) => ({ ...l }));
      receiptIds.value = inv.receipts.map((r) => r.id);
    };

    const applyDraft = (d: InvoiceDraft) => {
      supplierId.value = d.supplier_id;
      number.value = d.invoice_number ?? "";
      if (d.issue_date) issueDate.value = d.issue_date;
      dueDate.value = d.due_date ?? addDays(issueDate.value, 30);
      currency.value = d.currency ?? "";
      net.value = d.net_amount;
      vat.value = d.vat_amount;
      total.value = d.total;
      documentKey.value = d.document_key;
      lines.value = d.lines;
    };

    onMounted(async () => {
      try {
        const [sups, recs, inv] = await Promise.all([
          trpc.purchasing.suppliers.query(),
          trpc.purchasing.receipts.query({ limit: 200, offset: 0 }),
          props.invoiceId ? trpc.supplierInvoices.get.query({ id: props.invoiceId }) : null,
        ]);
        suppliers.value = sups.filter((s) => s.active);
        receipts.value = recs as ReceiptSummary[];
        if (inv) apply(inv as Invoice);
        else if (props.initial) applyDraft(props.initial);
        if (!inv && receiptIds.value.length && !supplierId.value) {
          supplierId.value = receipts.value.find((r) => r.id === receiptIds.value[0])?.supplier_id ?? null;
        }
        if (!inv && !props.initial && receiptIds.value.length) {
          total.value = Math.round(
            receipts.value.filter((r) => receiptIds.value.includes(r.id)).reduce((s, r) => s + r.total, 0) * 100,
          ) / 100;
          net.value = total.value;
        }
      } catch {
        toast.showToast(t("inventory.invoices.load_failed"), { type: "error" });
        props.onClose(false);
      } finally {
        loading.value = false;
      }
    });

    // With lines, the amounts follow them; without, they're typed in.
    const filledLines = computed(() => lines.value.filter((l) => l.description.trim() || lineTotal(l) != null));
    const derived = computed(() => {
      if (!filledLines.value.length) return null;
      let n = 0;
      let v = 0;
      for (const l of filledLines.value) {
        const lt = lineTotal(l) ?? 0;
        n += lt;
        v += Math.round(lt * (l.vat_rate ?? 0)) / 100;
      }
      return { net: n, vat: v, total: n + v };
    });
    const shownTotal = computed(() => derived.value?.total ?? total.value ?? ((net.value ?? 0) + (vat.value ?? 0) || null));
    const linesInvalid = computed(() => filledLines.value.some((l) => lineTotal(l) == null));
    const canSave = computed(() => !loading.value && !busy.value && !linesInvalid.value && shownTotal.value != null);

    const save = async () => {
      const saved = await trpc.supplierInvoices.save.mutate({
        id: id.value ?? undefined,
        supplier_id: supplierId.value,
        invoice_number: number.value.trim() || null,
        issue_date: issueDate.value,
        due_date: dueDate.value || null,
        currency: currency.value.trim() || null,
        net_amount: derived.value ? null : net.value,
        vat_amount: derived.value ? null : vat.value,
        total: derived.value ? null : (total.value ?? shownTotal.value),
        notes: notes.value.trim() || null,
        document_key: documentKey.value,
        receipt_ids: receiptIds.value,
        lines: filledLines.value.map((l) => ({
          description: l.description,
          supplier_code: l.supplier_code,
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
          vat_rate: l.vat_rate,
          line_total: lineTotal(l),
        })),
      });
      changed.value = true;
      apply(saved as Invoice);
      return saved as Invoice;
    };

    const run = async (action: () => Promise<void>, failKey: string) => {
      busy.value = true;
      try {
        await action();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t(failKey), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    const handleSave = () =>
      run(async () => {
        await save();
        toast.showToast(t("inventory.invoices.saved"), { type: "success" });
        props.onClose(true);
      }, "inventory.invoices.save_failed");

    const handlePaid = (payment: Payment) =>
      run(async () => {
        const saved = status.value === "open" ? await save() : invoice.value!;
        const paid = await trpc.supplierInvoices.markPaid.mutate({ id: saved.id, ...payment });
        apply(paid as Invoice);
        payOpen.value = false;
        toast.showToast(t("inventory.invoices.marked_paid"), { type: "success" });
        props.onClose(true);
      }, "inventory.invoices.save_failed");

    const setStatus = (next: "open" | "void") =>
      run(async () => {
        if (next === "void" && !confirm(t("inventory.invoices.void_confirm"))) return;
        const updated = await trpc.supplierInvoices.setStatus.mutate({ id: id.value!, status: next });
        apply(updated as Invoice);
        changed.value = true;
      }, "inventory.invoices.save_failed");

    const remove = () =>
      run(async () => {
        if (!confirm(t("inventory.invoices.delete_confirm"))) return;
        await trpc.supplierInvoices.delete.mutate({ id: id.value! });
        props.onClose(true);
      }, "inventory.invoices.delete_failed");

    const statusBanner = () => {
      const inv = invoice.value;
      if (status.value === "paid" && inv) {
        return (
          <div class="alert alert-success py-2 text-sm">
            <span class="material-symbols-outlined">check_circle</span>
            <span>
              {t("inventory.invoices.paid_banner")
                .replace("{date}", formatDay(inv.paid_at))
                .replace("{amount}", money(inv.paid_amount, inv.currency))
                .replace("{method}", inv.payment_method ? t(`inventory.invoices.method_${inv.payment_method}`) : "")}
              {inv.payment_reference && <span class="ml-1 text-base-content/60">· {inv.payment_reference}</span>}
            </span>
          </div>
        );
      }
      if (status.value === "void") {
        return <div class="alert py-2 text-sm">{t("inventory.invoices.void_banner")}</div>;
      }
      if (dueDate.value && id.value) {
        const days = daysUntil(dueDate.value);
        if (days < 0) {
          return (
            <div class="alert alert-error py-2 text-sm">
              {t("inventory.invoices.overdue_by").replace("{days}", String(-days))}
            </div>
          );
        }
      }
      return null;
    };

    const field = (label: string, input: unknown) => (
      <label class="block">
        <span class="mb-1 block text-sm font-medium">{label}</span>
        {input}
      </label>
    );

    const amountInput = (model: typeof net, disabled: boolean) => (
      <input
        type="number"
        step="0.01"
        class="input input-bordered input-sm w-full text-right"
        disabled={disabled}
        value={model.value ?? ""}
        onChange={(e: Event) => (model.value = numOrNull((e.target as HTMLInputElement).value))}
      />
    );

    const renderAmounts = () => {
      const d = derived.value;
      const ro = readOnly.value || d != null;
      return (
        <div class="grid grid-cols-3 gap-3 rounded-lg bg-base-200 p-3">
          {field(t("inventory.invoices.net"), d ? <div class="py-1 text-right">{d.net.toFixed(2)}</div> : amountInput(net, ro))}
          {field(t("inventory.invoices.vat"), d ? <div class="py-1 text-right">{d.vat.toFixed(2)}</div> : amountInput(vat, ro))}
          {field(
            t("inventory.invoices.total"),
            d ? (
              <div class="py-1 text-right text-lg font-semibold">{d.total.toFixed(2)}</div>
            ) : (
              <input
                type="number"
                step="0.01"
                class="input input-bordered input-sm w-full text-right font-semibold"
                disabled={ro}
                placeholder={net.value != null ? ((net.value ?? 0) + (vat.value ?? 0)).toFixed(2) : ""}
                value={total.value ?? ""}
                onChange={(e: Event) => (total.value = numOrNull((e.target as HTMLInputElement).value))}
              />
            ),
          )}
        </div>
      );
    };

    const renderBody = () => {
      if (loading.value) {
        return (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        );
      }
      return (
        <div class="space-y-4 p-6">
          {statusBanner()}
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field(
              t("inventory.purchases.supplier"),
              <select
                class="select select-bordered select-sm w-full"
                disabled={readOnly.value}
                value={supplierId.value ?? ""}
                onChange={(e: Event) => {
                  const v = (e.target as HTMLSelectElement).value;
                  supplierId.value = v ? Number(v) : null;
                  receiptIds.value = [];
                }}
              >
                <option value="">{t("inventory.purchases.no_supplier")}</option>
                {suppliers.value.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>,
            )}
            {field(
              t("inventory.invoices.number"),
              <input class="input input-bordered input-sm w-full" disabled={readOnly.value} v-model={number.value} />,
            )}
            {field(
              t("inventory.invoices.issue_date"),
              <input type="date" class="input input-bordered input-sm w-full" disabled={readOnly.value} v-model={issueDate.value} />,
            )}
            <div>
              {field(
                t("inventory.invoices.due_date"),
                <input type="date" class="input input-bordered input-sm w-full" disabled={readOnly.value} v-model={dueDate.value} />,
              )}
              {!readOnly.value && (
                <div class="mt-1 flex flex-wrap gap-1">
                  {TERMS.map((days) => (
                    <button
                      key={days}
                      type="button"
                      class={[
                        "btn btn-xs",
                        dueDate.value === addDays(issueDate.value, days) ? "btn-primary" : "btn-ghost",
                      ].join(" ")}
                      onClick={() => (dueDate.value = addDays(issueDate.value, days))}
                    >
                      {days === 0 ? t("inventory.invoices.terms_now") : t("inventory.invoices.terms_days").replace("{days}", String(days))}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {renderAmounts()}

          <div>
            <div class="mb-1 flex items-center justify-between">
              <span class="text-sm font-medium">{t("inventory.invoices.lines")}</span>
              {!readOnly.value && (
                <button class="btn btn-ghost btn-xs" onClick={() => (lines.value = [...lines.value, emptyInvoiceLine()])}>
                  <span class="material-symbols-outlined text-base">add</span>
                  {t("inventory.purchases.add_line")}
                </button>
              )}
            </div>
            {lines.value.length === 0 ? (
              <p class="text-xs text-base-content/50">{t("inventory.invoices.lines_optional")}</p>
            ) : (
              <InvoiceLinesTable
                lines={lines.value}
                readOnly={readOnly.value}
                onChange={(next: InvoiceLine[]) => (lines.value = next)}
              />
            )}
          </div>

          <LinkedDeliveries
            invoiceId={id.value}
            supplierId={supplierId.value}
            receipts={receipts.value}
            selected={receiptIds.value}
            readOnly={readOnly.value}
            onChange={(ids: number[]) => (receiptIds.value = ids)}
          />

          <div class="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_8rem]">
            {field(
              t("inventory.purchases.notes"),
              <textarea class="textarea textarea-bordered w-full text-sm" rows={2} disabled={readOnly.value} v-model={notes.value} />,
            )}
            {field(
              t("inventory.invoices.currency"),
              <input
                class="input input-bordered input-sm w-full uppercase"
                maxlength={3}
                placeholder="CHF"
                disabled={readOnly.value}
                v-model={currency.value}
              />,
            )}
          </div>
          {documentKey.value && (
            <a
              class="link link-primary inline-flex items-center gap-1 text-sm"
              href={`/api/purchasing/documents/${encodeURIComponent(documentKey.value)}`}
              target="_blank"
              rel="noopener"
            >
              <span class="material-symbols-outlined text-base">description</span>
              {t("inventory.scan.view_document")}
            </a>
          )}
        </div>
      );
    };

    const renderButtons = () => (
      <div class="border-t p-4">
        <div class="flex flex-wrap justify-between gap-2">
          <div class="flex gap-2">
            <button class="btn btn-neutral" onClick={() => props.onClose(changed.value)}>
              {readOnly.value ? t("inventory.purchases.close") : t("general.words.cancel")}
            </button>
            {id.value && status.value !== "paid" && (
              <button class="btn btn-ghost text-error" disabled={busy.value} onClick={remove}>
                {t("inventory.invoices.delete")}
              </button>
            )}
            {id.value && status.value === "open" && (
              <button class="btn btn-ghost" disabled={busy.value} onClick={() => setStatus("void")}>
                {t("inventory.invoices.void")}
              </button>
            )}
          </div>
          <div class="flex gap-2">
            {readOnly.value ? (
              <button class="btn btn-ghost" disabled={busy.value} onClick={() => setStatus("open")}>
                <span class="material-symbols-outlined text-base">undo</span>
                {t("inventory.invoices.reopen")}
              </button>
            ) : (
              <>
                <button class="btn btn-ghost" disabled={!canSave.value} onClick={handleSave}>
                  {t("inventory.invoices.save")}
                </button>
                <button class="btn btn-primary" disabled={!canSave.value} onClick={() => (payOpen.value = true)}>
                  <span class="material-symbols-outlined text-base">payments</span>
                  {t("inventory.invoices.save_and_pay")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );

    return () => (
      <>
        <LemmonModal
          title={id.value ? t("inventory.invoices.edit_title") : t("inventory.invoices.new_title")}
          onClose={() => props.onClose(changed.value)}
          renderButtons={renderButtons}
        >
          {renderBody()}
        </LemmonModal>
        {payOpen.value && (
          <MarkPaidModal
            total={shownTotal.value ?? 0}
            currency={currency.value || null}
            saving={busy.value}
            onClose={() => (payOpen.value = false)}
            onConfirm={handlePaid}
          />
        )}
      </>
    );
  },
});
